import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth, mutateJsonWithAuth } from '@/lib/api'
import type {
  AppTool,
  AppToolWrite,
  McpConnection,
  McpConnectionTestResult,
  McpConnectionWrite,
  WebhookAppToolWrite,
} from '@/features/tools/types'

export async function fetchOrgTools(
  token: string,
  signal?: AbortSignal
): Promise<AppTool[]> {
  const data = await fetchJsonWithAuth<{ tools: AppTool[] }>({
    token,
    url: `${AGENT_URL}/tools`,
    signal,
    errorMessage: 'Failed to fetch tools',
  })
  return data.tools
}

export async function createTool(
  token: string,
  tool: WebhookAppToolWrite
): Promise<AppTool> {
  const data = await mutateJsonWithAuth<{ tool: AppTool }>({
    token,
    url: `${AGENT_URL}/tools`,
    method: 'POST',
    body: tool,
    errorMessage: 'Failed to create tool',
  })
  return data.tool
}

export async function updateTool(
  token: string,
  toolId: string,
  tool: Partial<AppToolWrite>
): Promise<AppTool> {
  const data = await mutateJsonWithAuth<{ tool: AppTool }>({
    token,
    url: `${AGENT_URL}/tools/${toolId}`,
    method: 'PATCH',
    body: tool,
    errorMessage: 'Failed to update tool',
  })
  return data.tool
}

export async function deleteTool(token: string, toolId: string): Promise<void> {
  await mutateJsonWithAuth<void>({
    token,
    url: `${AGENT_URL}/tools/${toolId}`,
    method: 'DELETE',
    errorMessage: 'Failed to delete tool',
  })
}

export async function fetchMcpConnections(
  token: string,
  signal?: AbortSignal
): Promise<McpConnection[]> {
  const data = await fetchJsonWithAuth<{ connections: McpConnection[] }>({
    token,
    url: `${AGENT_URL}/tools/mcp-connections`,
    signal,
    errorMessage: 'Failed to fetch MCP connections',
  })
  return data.connections
}

export async function createMcpConnection(
  token: string,
  connection: McpConnectionWrite
): Promise<McpConnection> {
  const data = await mutateJsonWithAuth<{ connection: McpConnection }>({
    token,
    url: `${AGENT_URL}/tools/mcp-connections`,
    method: 'POST',
    body: connection,
    errorMessage: 'Failed to create MCP connection',
  })
  return data.connection
}

export async function updateMcpConnection(
  token: string,
  connectionId: string,
  connection: Partial<McpConnectionWrite>
): Promise<McpConnection> {
  const data = await mutateJsonWithAuth<{ connection: McpConnection }>({
    token,
    url: `${AGENT_URL}/tools/mcp-connections/${connectionId}`,
    method: 'PATCH',
    body: connection,
    errorMessage: 'Failed to update MCP connection',
  })
  return data.connection
}

export async function deleteMcpConnection(
  token: string,
  connectionId: string
): Promise<void> {
  await mutateJsonWithAuth<void>({
    token,
    url: `${AGENT_URL}/tools/mcp-connections/${connectionId}`,
    method: 'DELETE',
    errorMessage: 'Failed to delete MCP connection',
  })
}

export async function startMcpOauth(
  token: string,
  connectionId: string,
  redirectUri: string
): Promise<{ authorization_url: string; expires_at: string }> {
  return mutateJsonWithAuth({
    token,
    url: `${AGENT_URL}/tools/mcp-connections/${connectionId}/oauth/start`,
    method: 'POST',
    body: { redirect_uri: redirectUri },
    errorMessage: 'Failed to start MCP sign-in',
  })
}

export async function completeMcpOauth(
  token: string,
  code: string,
  state: string
): Promise<{
  grant: { id: string; connection_id: string }
  scopes: string[]
}> {
  return mutateJsonWithAuth({
    token,
    url: `${AGENT_URL}/tools/mcp-connections/oauth/complete`,
    method: 'POST',
    body: { code, state },
    errorMessage: 'Failed to complete MCP sign-in',
  })
}

export async function testMcpConnection(
  token: string,
  connectionId: string
): Promise<McpConnectionTestResult> {
  return mutateJsonWithAuth({
    token,
    url: `${AGENT_URL}/tools/mcp-connections/${connectionId}/test`,
    method: 'POST',
    errorMessage: 'Failed to test MCP connection',
  })
}
